import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Suspended: React.FC = () => {
  const { isAuthenticated, isSuspended, isActive, loading, logout, userProfile } = useAuth();

  // If not authenticated, redirect to login
  if (!loading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If active, redirect to home
  if (!loading && isAuthenticated && isActive) {
    return <Navigate to="/" replace />;
  }

  // Show loading while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100 dark:from-gray-900 dark:to-gray-800 px-4">
      <div className="max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-xl mb-4">
            <svg
              className="w-10 h-10 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">BankingLATAM</h1>
        </div>

        {/* Suspended Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          {/* Suspended Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full mb-6">
            <svg
              className="w-10 h-10 text-red-600 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            Account Suspended
          </h2>

          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Your account has been suspended. Please contact the BankingLATAM support team for
            assistance.
          </p>

          {/* User Info */}
          {userProfile && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Account</p>
              <p className="font-medium text-gray-900 dark:text-white">{userProfile.email}</p>
            </div>
          )}

          {/* Contact Info */}
          <div className="text-left bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-red-800 dark:text-red-300 mb-2">
              Need help?
            </h3>
            <p className="text-sm text-red-700 dark:text-red-400">
              If you believe this is an error or need to reinstate your account, please contact
              our support team.
            </p>
          </div>

          {/* Contact Button */}
          <a
            href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL || 'support@bankinglatam.com'}?subject=Account%20Suspension%20Inquiry`}
            className="block w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors mb-3"
          >
            Contact Support
          </a>

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="w-full py-3 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Suspended;
